import React, { 
  useCallback, useMemo, useState
} from 'react';

import capitalize from 'lodash/capitalize';

import compareDesc from 'date-fns/compareDesc';
import parseISO from 'date-fns/parseISO';

import { formatOrchestrationRunDate, getNextRunFromCronString } from '../../../utils/date_utils';
import getLogsForTaskRun from '../../../utils/api_functions/getLogsForTaskRun';
import { getRunTypeIcon } from '../../../utils/task_utils';

import AlternatingRowDataTable from '../../common/DataTable/AlternatingRowDataTable';
import Button from '../../common/Button/Button';
import CardHeader from '../../common/Labels/CardHeader';
import CardView from '../../common/CardView/CardView';
import Icon from '../../common/icons/Icon';
import Label from '../../common/Labels/Label';
import ModalOverlay from '../../common/ModalOverlay/ModalOverlay';

import ProjectRoadmapIcon from '../../common/icons/ProjectRoadmapIcon';
import TaskStatusBadge from '../TaskStatusBadge/TaskStatusBadge';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import { StyledOcticon } from '@primer/react';
import { LogIcon } from '@primer/octicons-react';
import ErrorLabel from '../../common/Labels/ErrorLabel';

const transformTaskListData = (taskListRows = []) => taskListRows?.map(taskListItem => ({
  id: taskListItem?.task,
  name: taskListItem?.name,
  createdAt: taskListItem?.created_at,
  lastRuns: taskListItem?.lastRuns,
  schedule: taskListItem?.schedule,
  status: capitalize(taskListItem?.status),
  runId: taskListItem?.run_id,
  runType: taskListItem?.run_type,
}))

const sortTaskData = (taskRows = []) => taskRows.sort((itemA, itemB) => {
  const firstDate = parseISO(itemA.createdAt);
  const secondDate = parseISO(itemB.createdAt);

  // 1 if firstDate < secondDate, -1 if firstDate > secondDate
  return compareDesc(firstDate, secondDate);
})

// Oneshot, CronJob, NetworkService
const RunTypeIndicator = ({ runType }) => {
  const chosenIcon = getRunTypeIcon(runType);
  return (
    <Icon
      icon={chosenIcon}
      fill="text-brand"
    />
  )
}

const LastRunAtCellRenderer = ({ row, value }) => {
  const lastRunDate = value && row?.original?.createdAt;
  const nextRunDate = row?.original?.schedule && getNextRunFromCronString(row?.original?.schedule, lastRunDate ? lastRunDate : new Date());
  const schedule = row?.original?.schedule

  const calculateDateToBeDisplayed = () => { 
    if (!lastRunDate) return '--'
    
    return `${formatOrchestrationRunDate(lastRunDate)}`
  }
  
  return (
    <div className="flex flex-col p-2">
      <div className="flex flex-col flex-wrap items-start mb-2">
        <div className="flex">
          <Label>{calculateDateToBeDisplayed()}</Label>
        </div>
        {
          schedule && nextRunDate && <Label size="sm">Next Scheduled Run: {formatOrchestrationRunDate(nextRunDate)}</Label>
        }
      </div>
    </div>
  )
}

const StatusCellRenderer = ({ row }) => {
  const status = row?.original?.status;
  return <TaskStatusBadge status={status} />
}

const TaskCellRenderer = ({ row, value }) => {
  const name = row?.original?.name;
  const runType = row?.original?.runType;

  return (
    <div className="flex flex-col p-2">
      <div className="flex flex-col flex-wrap items-start mb-2">
        <div className="flex items-center justify-center">
          <RunTypeIndicator runType={runType} />
          <Label
            className="ml-2"
            size="md"
            title={value}
            bold
          >
            {name}
          </Label>
        </div>
        <Label size="md" title={value}>
          {value}
        </Label>
      </div>
    </div>
  )
}

const ActionCellRenderer = ({ 
  row, meta = {}
}) => {
  const buttonStyle = {
    padding: 0,
    border: 'none',
    boxShadow: 'none',
  }

  return (
    <div className="flex flex-col items-center">
      <Button sx={buttonStyle} onClick={useCallback(() => {
        meta?.fetchLogsForTaskRun(row?.original?.runId)
        meta?.setViewLog(row?.original?.runId)
      }, [meta, row?.original?.runId])}>
        <Icon
          icon={ProjectRoadmapIcon}
          fill="text-brand"
          width="16"
          height="16"
        />
      </Button>  
    </div> 
  )
}

export const TaskRunListTable = ({ allTaskRuns = [] }) => {
  const [viewLog, setViewLog] = useState(false)
  const [logs, setLog] = useState(null)
  const [isLoading, setLoading] = useState(false);

  const data = useMemo(() => sortTaskData(transformTaskListData(allTaskRuns)), [allTaskRuns]);

  const columns = useMemo(() => [
    {
      Header: 'Task Name',
      accessor: 'id',
      Cell: TaskCellRenderer,
      width: 100
    },
    {
      Header: 'Run Status',
      accessor: 'status',
      Cell: StatusCellRenderer,
      width: 60
    },
    {
      Header: 'Last Run at',
      accessor: 'lastRuns',
      Cell: LastRunAtCellRenderer,
      width: 100,
      tooltipText: 'Next scheduled time calculated according to CRON settings. Actual run time might differ due to multiple factors.',
    },
    {
      Header: 'Logs',
      accessor: 'logs',
      Cell: ActionCellRenderer,
      width: 40,
      headerStyle: { textAlign: 'right' }
    }
  ], []);

  const defaultColumn = React.useMemo(
    () => ({
      width: 160,
    }),
    []
  )

  const fetchLogsForTaskRun = useCallback(async (runId) => {
    setLoading(true);
    await getLogsForTaskRun(runId).then(res => {
      setLoading(false);
      setLog(res?.logs)
    })
  }, [])

  const onRefreshLogs = useCallback(async () => {
    if (viewLog) {
      fetchLogsForTaskRun(viewLog);
    }
  }, [fetchLogsForTaskRun, viewLog])

  const onModalToggle = useCallback((keepOpen) => {
    if (!keepOpen) {
      setViewLog(false);
    }
  }, [])

  return (
    <>
      <AlternatingRowDataTable
        id="taskrun-data-table"
        columns={columns}
        data={data}
        defaultColumn={defaultColumn}
        pagination={null}
        meta={{
          setLog,
          setViewLog,
          fetchLogsForTaskRun,
        }}
      />
      {viewLog && <ModalOverlay active={viewLog} onModalToggle={onModalToggle}>
        <div style={{ width: '850px', height: '450px' }}>
          <CardView className="flex flex-col justify-evenly relative pt-0">
            <div className="flex justify-between items-center mb-2">
              <CardHeader className="my-4" size="2xl">View Task Run Log</CardHeader>
              <Button
                variant="close"
                iconSize={24}
                textColor="border-dark"
                hoverTextColor="border-darker"
                activeTextColor="info"
                onClick={() => setViewLog(false)}
              />
            </div>
            <div className="flex flex-col border-solid border-2 border-black-500 overflow-scroll" style={{ minHeight: '300px' }}>
              {
                logs?.length ? logs.map(log => <span>{log}</span>) : (
                  <div className="p-4 relative h-full flex flex-col justify-center items-center">
                    { isLoading ? <LoadingSpinner animationKey="task-run-logs-loading-spinner" useOverlay /> : null }
                    <StyledOcticon
                      className="mb-4 text-brand"
                      icon={LogIcon}
                      size={64}
                    />
                    <ErrorLabel className="flex justify-center items-center" size="2xl">
                      No logs available
                    </ErrorLabel>
                  </div>
                )
              }
            </div>
            <div className="flex flex-end flex-row-reverse w-full mt-2">
              <Button onClick={onRefreshLogs}>Refresh Log</Button>
            </div>
          </CardView>
        </div>
      </ModalOverlay>}
    </>
  )
};

export default TaskRunListTable;
